<template>
  <div class="main-atv">
    <template v-if="!acaoId">
      <Breadcrumb :items="breadcrumbItems" />

      <section class="parcerias-informativo" aria-labelledby="titulo-informativo">
        <div class="parcerias-conteudo">
          <div class="informativo-conteudo">
            <h3 id="titulo-informativo">
              Juntos por um impacto ainda maior!
            </h3>

            <p>
              As parcerias com a UFC são fundamentais para ampliar o alcance
              das ações de extensão, fortalecer a integração com a comunidade
              e gerar mais oportunidades de aprendizado, pesquisa e
              desenvolvimento. Ao fazer parceria com a PREX, você contribui
              para transformar conhecimento em impacto social.
            </p>

            <div class="btn-informativo-conteudo">
              <button type="button" class="btn-informativo" @click="modalFluxoAberto = true">
                Veja o fluxo de parceria
              </button>
            </div>
          </div>

          <div class="informativo-logo">
            <img :src="imagemPadrao" alt="Universidade Federal do Ceará" />
          </div>
        </div>
      </section>

      <div v-if="modalFluxoAberto" class="overlay" @click.self="modalFluxoAberto = false">
        <section class="modal-fluxo" role="dialog" aria-modal="true" aria-labelledby="titulo-modal-fluxo" tabindex="-1">
          <header class="modal-header">
            <span id="titulo-modal-fluxo" class="modal-label">
              Processo de solicitação de parceria
            </span>

            <button type="button" class="btn-fechar-modal" aria-label="Fechar modal" @click="modalFluxoAberto = false">
              ×
            </button>
          </header>

          <div class="modal-fluxo-conteudo">
            <img :src="fluxo"
              alt="Fluxograma com as etapas de solicitação, análise, aprovação e formalização de uma parceria com a PREX."
              class="imagem-fluxo" />
          </div>

          <footer class="modal-fluxo-acoes">
            <button type="button" class="btn-informativo" @click="modalFluxoAberto = false">
              Fechar
            </button>
          </footer>
        </section>
      </div>

      <div class="header-menu-parcerias">
        <h2 class="titulo-pagina">Parcerias da Extensão</h2>

        <div class="filtros-listagem">
          <FiltrosComponent :isCoordenador="isCoordenador" :nomeTela="'parceria'" :filtrosIniciais="filtrosAtivos"
            :termoBuscaInicial="termoBusca" @aplicarFiltros="onAplicarFiltros" @pesquisar="onPesquisar" />
        </div>
      </div>
    </template>

    <FiltrosComponent v-if="acaoId" :isCoordenador="isCoordenador" :nomeTela="'parceria'"
      :filtrosIniciais="filtrosAtivos" :termoBuscaInicial="termoBusca" @aplicarFiltros="onAplicarFiltros"
      @pesquisar="onPesquisar" />
  </div>

  <Carregamento v-if="isLoading" />

  <div v-else id="parcerias" :class="{ 'single-item': parcerias.length === 1 }">
    <div v-for="parceria in parcerias" :key="parceria.id" class="parceria-card"
      :class="{ 'card-sem-acao': !parceria.acaoId }" :role="parceria.acaoId ? 'link' : undefined"
      :tabindex="parceria.acaoId ? 0 : undefined" @click="abrirAcao(parceria)" @keydown.enter="abrirAcao(parceria)"
      @keydown.space.prevent="abrirAcao(parceria)">
      <img class="card-img-top" :src="parceria.imagemUrl || imagemPadrao" :alt="parceria.tituloAcao"
        @error="usarImagemPadrao" />

      <div class="card-body">
        <div class="card-head">
          <h5>{{ parceria.tituloAcao.toUpperCase() }}</h5>

          <div v-if="
            parceria.totalFormalizadas > 0 ||
            parceria.totalNaoFormalizadas > 0
          " class="tags">
            <p v-if="parceria.totalFormalizadas > 0" id="formalizada" class="formalizada">
              {{ parceria.totalFormalizadas }} formalizada(s)
            </p>

            <p v-if="parceria.totalNaoFormalizadas > 0" id="nao-formalizada" class="nao-formalizada">
              {{ parceria.totalNaoFormalizadas }} não formalizada(s)
            </p>
          </div>

          <p v-if="parceria.area">
            <strong>Área temática:</strong>
            {{ parceria.area }}
          </p>
        </div>
      </div>

      <div v-if="parceria.acaoId" class="botoes">
        <button type="button" class="btn btn-primary inscrever" @click.stop="abrirAcao(parceria)">
          Saiba Mais
        </button>
      </div>
    </div>
  </div>

  <AvisoListagem v-if="!isLoading && parcerias.length === 0">
    Nenhuma parceria encontrada
  </AvisoListagem>

  <PaginacaoComponent v-if="!isLoading && totalPaginas > 1" :total-paginas="totalPaginas" :pagina-atual="paginaAtual"
    @mudar-pagina="carregarPagina" />
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";

import Breadcrumb from "@/components/common/Breadcrumb.vue";
import FiltrosComponent from "@/components/common/FiltrosComponent.vue";
import PaginacaoComponent from "@/components/common/paginacaoComponent.vue";
import AvisoListagem from "@/components/common/AvisoListagem.vue";
import Carregamento from "@/components/common/Carregamento.vue";

import imagemPadrao from "@/assets/logo1prex.png";
import fluxo from "@/assets/fluxo.png";

import parceriaService from "@/service/parceriaService";

const props = defineProps({
  isCoordenador: Boolean,
  acaoId: String,
});

const router = useRouter();
const route = useRoute();

const isLoading = ref(false);
const modalFluxoAberto = ref(false);
const parcerias = ref([]);

const acaoId = computed(() => props.acaoId || route.params.id || null);

const termoBusca = ref(route.query.q || "");

const filtrosAtivos = ref({
  unidadeId: route.query.unidadeId || null,
  area: route.query.area || null,
  jaFormalizada: route.query.jaFormalizada ?? null,
});

const paginaAtual = ref(Number(route.query.page) || 0);
const itensPorPagina = 6;
const totalPaginas = ref(0);

const breadcrumbItems = computed(() => [
  { label: "Início", to: "/" },
  { label: "Parcerias", to: null },
]);

function onAplicarFiltros(filtros) {
  filtrosAtivos.value = {
    ...filtrosAtivos.value,
    ...filtros,
  };

  paginaAtual.value = 0;
  atualizarQuery();
}

function onPesquisar(termo) {
  termoBusca.value = termo || "";
  paginaAtual.value = 0;
  atualizarQuery();
}

function atualizarQuery() {
  const query = { ...route.query };

  if (termoBusca.value.trim()) {
    query.q = termoBusca.value.trim();
  } else {
    delete query.q;
  }

  if (filtrosAtivos.value.unidadeId) {
    query.unidadeId = filtrosAtivos.value.unidadeId;
  } else {
    delete query.unidadeId;
  }

  if (filtrosAtivos.value.area) {
    query.area = filtrosAtivos.value.area;
  } else {
    delete query.area;
  }
  if (
    filtrosAtivos.value.jaFormalizada !== null &&
    filtrosAtivos.value.jaFormalizada !== ""
  ) {
    query.jaFormalizada = String(
      filtrosAtivos.value.jaFormalizada
    );
  } else {
    delete query.jaFormalizada;
  }

  delete query.page;

  router.push({ query });
}

function carregarPagina(pagina) {
  paginaAtual.value = pagina;

  const query = { ...route.query };

  if (pagina === 0) {
    delete query.page;
  } else {
    query.page = String(pagina);
  }

  router.push({ query });
}

function usarImagemPadrao(event) {
  event.target.onerror = null;
  event.target.src = imagemPadrao;
}

function abrirAcao(parceria) {
  if (!parceria?.acaoId) return;

  router.push(`/acao/${parceria.acaoId}/informacoes`);
}

async function carregarParcerias() {
  isLoading.value = true;

  try {
    const parametros = {
      page: paginaAtual.value,
      size: itensPorPagina,
      ativa: true,
    };

    if (termoBusca.value.trim()) {
      parametros.busca = termoBusca.value.trim();
    }

    if (filtrosAtivos.value.area) {
      parametros.areaAtuacao = filtrosAtivos.value.area;
    }

    if (filtrosAtivos.value.unidadeId) {
      parametros.unidadeId = filtrosAtivos.value.unidadeId;
    }
    if (
      filtrosAtivos.value.jaFormalizada !== null &&
      filtrosAtivos.value.jaFormalizada !== ""
    ) {
      parametros.jaFormalizada =
        filtrosAtivos.value.jaFormalizada === true ||
        filtrosAtivos.value.jaFormalizada === "true";
    }


    if (acaoId.value) {
      parametros.acaoExtensaoId = acaoId.value;
    }

    const response =
      await parceriaService.listarParceriasExternas(parametros);

    const dados = response?.data ?? response;
    const conteudo = Array.isArray(dados) ? dados : dados?.content ?? [];

    parcerias.value = conteudo.map((p) => ({
      id: p.id,
      acaoId: p.acaoId || null,
      tituloAcao: p.tituloAcao || "Ação sem título",
      area: p.areaTematicaPrincipal || null,
      totalFormalizadas: Number(p.totalFormalizadas) || 0,
      totalNaoFormalizadas: Number(p.totalNaoFormalizadas) || 0,
      imagemUrl: p.imagemUrl || null,
    }));

    totalPaginas.value = Array.isArray(dados)
      ? conteudo.length > 0
        ? 1
        : 0
      : Number(dados?.totalPages) || 0;

    if (
      totalPaginas.value > 0 &&
      paginaAtual.value >= totalPaginas.value
    ) {
      const ultimaPagina = totalPaginas.value - 1;
      paginaAtual.value = ultimaPagina;

      await router.replace({
        query: {
          ...route.query,
          ...(ultimaPagina === 0
            ? { page: undefined }
            : { page: String(ultimaPagina) }),
        },
      });

      return;
    }
  } catch (error) {
    console.error("Erro ao carregar parcerias externas:", error);
    parcerias.value = [];
    totalPaginas.value = 0;
  } finally {
    isLoading.value = false;
  }
}

watch(
  [acaoId, () => route.fullPath],
  async () => {
    paginaAtual.value = Number(route.query.page) || 0;
    termoBusca.value = route.query.q || "";

    filtrosAtivos.value = {
      unidadeId: route.query.unidadeId || null,
      area: route.query.area || null,
      jaFormalizada: route.query.jaFormalizada ?? null,
    };

    await carregarParcerias();
  },
  { immediate: true },
);
</script>

<style scoped>
.main-atv {
  width: 100%;
  max-width: none;
}

.header-menu-parcerias {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid lightgray;
  width: 100%;
  height: min-content;
}

.titulo-pagina {
  width: 100%;
  margin: 0;
  padding-bottom: 0;
}

.filtros-listagem {
  width: 100%;
  margin-bottom: 0;
}

.filtros-listagem :deep(.barra-acoes) {
  margin-bottom: 0 !important;
  justify-content: flex-end !important;
  gap: 0 !important;
  padding: 0;
}

.parcerias-informativo {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 3rem;
}

.parcerias-conteudo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.informativo-conteudo {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 60%;
}

.informativo-conteudo h3 {
  margin: 0 0 8px;
  color: var(--preto);
  font-size: 1.25rem;
  font-weight: 750;
  line-height: 1.3;
}

.informativo-conteudo p {
  margin: 0 0 12px;
  color: var(--cinza-escuro);
  font-size: 0.94rem;
  line-height: 1.6;
}

.btn-informativo-conteudo {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.btn-informativo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 15px;
  border: 0;
  border-radius: 7px;
  background: var(--roxo-primario);
  color: var(--branco);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.btn-informativo:hover {
  background: var(--roxo-medio);
}

.btn-informativo:focus-visible {
  outline: 2px solid var(--roxo-primario);
  outline-offset: 3px;
}

.informativo-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25%;
  min-height: 100px;
}

.informativo-logo img {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 300px;
  object-fit: contain;
}

.modal-fluxo {
  display: flex;
  flex-direction: column;
  width: min(1100px, 90%);
  max-height: 92vh;
  max-height: 92dvh;
  min-height: 0;
  overflow: hidden;
  border-radius: 16px;
  color: var(--preto);
}

.modal-fluxo-conteudo {
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 16px;
  background: var(--branco);
}

.imagem-fluxo {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  margin: 0 auto;
  object-fit: contain;
  object-position: center;
  border-radius: 8px;
}

.modal-fluxo-acoes {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid var(--roxo-superficie);
  background: var(--branco);
}

#parcerias {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: none;
}

#parcerias.single-item {
  grid-template-columns: minmax(0, 1fr);
}

.parceria-card {
  display: flex;
  position: relative;
  width: 100%;
  height: 235px;
  max-height: 235px;
  overflow: hidden;
  background-color: var(--branco);
  color: var(--preto);
  border: 1.5px solid var(--roxo-superficie);
  border-radius: 12px;
  cursor: pointer;
  box-shadow:
    0 4px 16px -2px hsla(258, 53%, 28%, 0.08),
    0 2px 6px -1px hsla(258, 53%, 28%, 0.04);
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.parceria-card:hover {
  transform: translateY(-2px);
  border-color: var(--roxo-claro);
  box-shadow:
    0 10px 24px -3px hsla(258, 53%, 28%, 0.14),
    0 4px 10px -2px hsla(258, 53%, 28%, 0.06);
}

.parceria-card:focus-visible {
  outline: 2px solid var(--roxo-primario);
  outline-offset: 3px;
}

.card-img-top {
  width: 200px;
  min-width: 200px;
  max-width: 208px;
  height: 215px;
  max-height: 215px;
  flex-shrink: 0;
  margin: 10px 0 10px 10px;
  padding: 5px;
  object-fit: contain;
  background-color: var(--branco);
  border: 1px solid var(--roxo-superficie);
  border-radius: 8px;
}

.card-body {
  display: flex;
  flex-direction: column;
  height: 235px;
  max-height: 235px;
  min-width: 0;
  padding: 8px 12px;
  overflow: hidden;
}

.card-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  min-width: 0;
}

.card-head h5 {
  display: -webkit-box;
  margin: 0 0 2px;
  overflow: hidden;
  overflow-wrap: anywhere;
  color: var(--roxo-primario);
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.25;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-head p {
  margin: 0;
  padding: 0;
  overflow-wrap: anywhere;
  color: var(--roxo-primario);
  font-size: 14px;
  line-height: 1.38;
}

.card-head strong {
  color: var(--roxo-primario);
}

.botoes {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 14px;
  border: none;
  border-radius: 6px;
  background-color: var(--roxo-primario);
  color: var(--branco);
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 1px 3px hsla(0, 0%, 0%, 0.08);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
  background-color: var(--roxo-medio);
}

.tags {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

#formalizada,
#nao-formalizada {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
}

#formalizada {
  background: var(--roxo-medio);
  color: var(--branco);
  border: 1px solid var(--roxo-primario);
}

#nao-formalizada {
  background: var(--cinza-claro);
  color: var(--cinza-escuro);
  border: 1px solid var(--cinza-medio);
}

@media (max-width: 768px) {
  .parcerias-informativo {
    width: 100%;
    gap: 14px;
    padding: 0;
  }

  .parcerias-conteudo {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }

  .informativo-conteudo {
    width: 100%;
  }

  .informativo-logo {
    width: 40%;
  }

  .btn-informativo-conteudo {
    justify-content: center;
  }

  .modal-fluxo {
    padding: 0;
  }

  .header-menu-parcerias {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .titulo-pagina {
    font-size: 1.4rem;
  }

  #parcerias {
    grid-template-columns: 1fr;
    gap: 1rem;
    width: 100%;
  }

  .parceria-card {
    flex-direction: column;
    width: 100%;
    height: auto;
    max-height: none;
    min-height: 0;
  }

  .card-img-top {
    width: calc(100% - 20px);
    min-width: 0;
    max-width: none;
    height: 200px;
    max-height: 200px;
    margin: 10px;
  }

  .card-body {
    width: 100%;
    height: auto;
    max-height: none;
    padding: 12px 14px 60px;
    overflow: visible;
  }

  .card-head h5 {
    font-size: 1rem;
    line-height: 1.3;
    -webkit-line-clamp: 3;
  }

  .card-head p {
    font-size: 14px;
    line-height: 1.4;
  }

  .botoes {
    right: 14px;
    bottom: 12px;
    left: 14px;
    display: flex;
  }

  .btn {
    width: 100%;
    min-height: 38px;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .titulo-pagina {
    font-size: 1.25rem;
  }

  .card-img-top {
    height: 170px;
    max-height: 170px;
  }

  .card-body {
    padding: 10px 12px 58px;
  }

  .card-head h5 {
    font-size: 0.98rem;
  }

  .card-head p {
    font-size: 13px;
  }
}
</style>
